import { useMemo } from 'react'

import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useLocalizedPlaces } from 'hooks/usePlace'
import { EmptyArray } from 'lib/array/arrayUtils'
import {
  getSearchVerticalTabsDestinationIds,
  isSearchVerticalTabsEligible,
} from './searchVerticalTabsEligibility'

interface Params {
  canShowToggle: boolean
  canActivateExperiment?: boolean
  destinationId?: string
  destinationIds?: Array<string>
  destinationName?: string
  rooms?: Array<App.Occupants>
}

function useHomesAndVillasToggleInSearchExperiment(params: Params) {
  const {
    canActivateExperiment = true,
    canShowToggle,
    destinationId,
    destinationIds,
    destinationName,
    rooms,
  } = params
  const destinationPlaceIds = useMemo(() => {
    if (!canShowToggle) return EmptyArray
    return getSearchVerticalTabsDestinationIds({ destinationId, destinationIds })
  }, [canShowToggle, destinationId, destinationIds])
  const destinationPlacesById = useLocalizedPlaces(destinationPlaceIds)
  const destinationPlaces = useMemo(() => (
    destinationPlaceIds.map(destinationPlaceId => destinationPlacesById[destinationPlaceId]?.place)
  ), [destinationPlaceIds, destinationPlacesById])
  const isActive = useMemo(() => (
    isSearchVerticalTabsEligible({
      destinationId,
      destinationIds,
      destinationName,
      destinationPlaces,
      rooms,
    })
  ), [destinationId, destinationIds, destinationName, destinationPlaces, rooms])

  const { isAnyVariant } = useExperiment(
    Experiments.nhaHomesAndVillasToggleInSearch,
    { canActivate: canActivateExperiment && isActive && canShowToggle },
  )

  return useMemo(() => ({
    isActive,
    isAnyVariant,
    shouldShowToggle: isActive && canShowToggle && isAnyVariant,
  }), [canShowToggle, isActive, isAnyVariant])
}

export default useHomesAndVillasToggleInSearchExperiment
